<script setup lang="ts">
import { defineComponent, shallowRef, type Component } from 'vue'
import { useIsOrganigrammeEditing } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import SourceBadge from '~/components/ui/SourceBadge.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import OrgaElementHoverTrigger from '~/components/orga/OrgaElementHoverTrigger.vue'
import groupEditIconUrl from '~/assets/icons/group_edit.svg?url'
import { ASYNC_OVERLAY_DELAY_MS } from '~/utils/defineAsyncWithSkeleton'
import {
  type OrgaAddEntityType,
  buildOrgaElementHeaderActions,
  hasOrgaElementHoverCard,
  resolveOrgaAddButtonLabel,
  resolveOrgaElementBlock,
  resolveOrgaHeaderMenuSelect,
  resolveOrgaModifyLabel,
} from '~/utils/orgaElementHeaderMenu'

const OrgaElementSlotPassthrough = defineComponent({
  name: 'OrgaElementSlotPassthrough',
  inheritAttrs: false,
  setup(_props, { slots }) {
    return () => slots.trigger?.() ?? slots.default?.()
  },
})

const OrgaElementHoverCard = shallowRef<Component>(OrgaElementSlotPassthrough)
const OrgaHeaderMenu = shallowRef<Component>(OrgaElementSlotPassthrough)
let hoverCardLoadStarted = false
let headerMenuLoadStarted = false

function scheduleIdleLoad(callback: () => void): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout: ASYNC_OVERLAY_DELAY_MS + 800 })
    return
  }
  window.setTimeout(callback, ASYNC_OVERLAY_DELAY_MS)
}

function loadOrgaElementHoverCard(): void {
  if (hoverCardLoadStarted) {
    return
  }
  hoverCardLoadStarted = true
  void import('~/components/orga/OrgaElementHoverCard.vue').then((mod) => {
    OrgaElementHoverCard.value = mod.default
  })
}

function loadOrgaHeaderMenu(): void {
  if (headerMenuLoadStarted) {
    return
  }
  headerMenuLoadStarted = true
  void import('~/components/orga/OrgaHeaderMenu.vue').then((mod) => {
    OrgaHeaderMenu.value = mod.default
  })
}

const isEditing = useIsOrganigrammeEditing()
const isAddHovered = ref(false)

export type AddEntityType = OrgaAddEntityType

type Size = 'xsmall' | 'small' | 'medium' | 'large'
type BadgeVariant = 'primary' | 'secondary' | 'neutral'
type ButtonType = 'primary' | 'secondary' | 'tertiary'
type Status = 'normal' | 'add'
type HeaderFlag = boolean

interface OrgaElementProps {
  label?: string
  libLong?: string | null
  desc?: string | null
  to?: string
  size?: Size
  badgeVariant?: BadgeVariant
  buttonType?: ButtonType
  disabled?: boolean
  header?: HeaderFlag
  status?: Status
  addEntityType?: AddEntityType
  addContext?: Record<string, string | undefined>
  addLabel?: string
  modifyKind?: 'direction' | 'domaine' | 'equipe' | 'agence'
  entityModifyKind?: 'direction' | 'domaine'
  compactModify?: boolean
  /** Carte du périmètre de l’utilisateur : contour visible en mode édition. */
  inScope?: boolean
  /** Carte hors périmètre : atténuée pour laisser voir ce qui est modifiable. */
  muted?: boolean
  /** Remplace « Modifier le CODIR » (hover / aria) lorsque modifyKind === equipe */
  modifyEquipeLabel?: string
  /** Remplace « Accéder au CODIR » dans le menu en-tête lorsqu’une équipe existe */
  accessEquipeLabel?: string
  /** Masque navigation et surcouches d’édition pendant une réorganisation. */
  reorderMode?: boolean
}

const emit = defineEmits<{
  (e: 'add-click', payload: { entityType: AddEntityType; context?: Record<string, string | undefined> }): void
  (e: 'modify-click', context?: Record<string, string | undefined>): void
}>()

const props = withDefaults(defineProps<OrgaElementProps>(), {
  label: undefined,
  libLong: undefined,
  desc: undefined,
  to: undefined,
  size: 'medium',
  badgeVariant: 'secondary',
  buttonType: 'primary',
  disabled: false,
  header: false,
  status: 'normal',
  addEntityType: undefined,
  addContext: undefined,
  addLabel: undefined,
  modifyKind: undefined,
  entityModifyKind: undefined,
  compactModify: false,
  inScope: false,
  muted: false,
  modifyEquipeLabel: undefined,
  accessEquipeLabel: undefined,
  reorderMode: false,
})

const isModifyHovered = ref(false)
const reorderLocked = computed(() => props.reorderMode)
const headerMenuActions = computed(() =>
  buildOrgaElementHeaderActions({ ...props, groupEditIconUrl }),
)
const block = computed(() =>
  resolveOrgaElementBlock({
    status: props.status,
    label: props.label,
    to: reorderLocked.value ? undefined : props.to,
    header: props.header,
    isEditing: isEditing.value && !reorderLocked.value,
    headerMenuCount: headerMenuActions.value.length,
  }),
)
const hasHoverCard = computed(() => hasOrgaElementHoverCard(props.libLong, props.desc))

onMounted(() => {
  if (hasHoverCard.value) {
    scheduleIdleLoad(loadOrgaElementHoverCard)
  }
  if (isEditing.value) {
    scheduleIdleLoad(loadOrgaHeaderMenu)
  }
})

watch(isEditing, (editing) => {
  if (editing) {
    loadOrgaHeaderMenu()
  }
})

watch(hasHoverCard, (enabled) => {
  if (enabled) {
    scheduleIdleLoad(loadOrgaElementHoverCard)
  }
})

function goTo() {
  if (reorderLocked.value || !props.to || props.disabled) {
    return
  }
  navigateTo(props.to)
}

function onAddClick() {
  if (props.addEntityType) {
    emit('add-click', {entityType: props.addEntityType, context: props.addContext})
  }
}

function onModifyClick(e: MouseEvent) {
  e.preventDefault()
  e.stopPropagation()
  emit('modify-click', {...props.addContext, modifyKind: props.modifyKind} as Record<string, string | undefined>)
}

const modifyIcon = computed(() => (props.modifyKind === 'equipe' ? null : 'box_edit'))
const addIcon = computed(() => (props.addEntityType === 'equipeDirection' ? 'group_add' : 'add'))
const showModifyOnHover = computed(
  () => !reorderLocked.value && isEditing.value && !!props.modifyKind && isModifyHovered.value,
)
const modifyLabel = computed(() => resolveOrgaModifyLabel(props.modifyKind, props.modifyEquipeLabel))
const addButtonLabel = computed(() => resolveOrgaAddButtonLabel(props.addEntityType, props.addLabel))
const headerMenuBadgeVariant = computed(() => (props.to ? 'primary' : props.badgeVariant) as BadgeVariant)

function applyHeaderMenuSelect(actionId: string) {
  const result = resolveOrgaHeaderMenuSelect(actionId, props)
  if (result?.kind === 'navigate') {
    navigateTo(result.to)
    return
  }
  if (result?.kind === 'modify') {
    emit('modify-click', result.context)
    return
  }
  if (result?.kind === 'add') {
    emit('add-click', { entityType: result.entityType, context: result.context })
  }
}
</script>

<template>
  <div
      v-if="block === 'add'"
      class="orga-element-add-wrapper"
      :class="{ 'is-add-hovered': isAddHovered }"
      @mouseenter="isAddHovered = true"
      @mouseleave="isAddHovered = false"
  >
    <SourceButton
        :icon="addIcon"
        :text="addButtonLabel"
        type="add"
        :size="size"
        class="orga-element block-content orga-element-add-btn"
        @click="onAddClick"
    />
  </div>

  <div v-else-if="block === 'skeleton'" class="orga-element skeleton-block" aria-busy="true">
    <div class="skeleton-inner"/>
  </div>

  <div
      v-else-if="block === 'header-badge-menu'"
      class="orga-element-header-badge-wrapper"
  >
    <component
        :is="OrgaHeaderMenu"
        :label="label ?? ''"
        :actions="headerMenuActions"
        :size="size"
        :badge-variant="headerMenuBadgeVariant"
        @select="applyHeaderMenuSelect"
    >
      <template #trigger>
        <SourceBadge
            :label="label ?? ''"
            :size="size"
            :variant="headerMenuBadgeVariant"
            class="orga-element block-content"
        />
      </template>
    </component>
  </div>

  <div
      v-else-if="block === 'header-badge'"
      class="orga-element-header-badge-wrapper"
  >
    <OrgaElementHoverTrigger
        :enabled="hasHoverCard"
        :hover-card="OrgaElementHoverCard"
        :label="label"
        :lib-long="libLong"
        :desc="desc"
    >
      <SourceBadge
          :label="label ?? ''"
          :size="size"
          :variant="badgeVariant"
          class="orga-element block-content"
      />
    </OrgaElementHoverTrigger>
  </div>

  <OrgaElementHoverTrigger
      v-else-if="block === 'badge'"
      :enabled="hasHoverCard"
      :hover-card="OrgaElementHoverCard"
      :label="label"
      :lib-long="libLong"
      :desc="desc"
  >
    <SourceBadge
        :label="label ?? ''"
        :size="size"
        :variant="badgeVariant"
        class="orga-element block-content"
    />
  </OrgaElementHoverTrigger>

  <div
      v-else-if="block === 'header-button-menu'"
      class="orga-element-header-badge-wrapper"
  >
    <component
        :is="OrgaHeaderMenu"
        :label="label ?? ''"
        :actions="headerMenuActions"
        :size="size"
        :badge-variant="headerMenuBadgeVariant"
        @select="applyHeaderMenuSelect"
    >
      <template #trigger>
        <SourceButton
            :text="label ?? ''"
            :size="size"
            :type="buttonType"
            :disabled="disabled"
            class="orga-element block-content"
            @click.prevent
        />
      </template>
    </component>
  </div>

  <div
      v-else
      class="orga-element-modify-wrapper"
      :class="{
        'is-modify-hovered': showModifyOnHover,
        'orga-element-modify-wrapper--in-scope': inScope && isEditing && !!modifyKind,
        'orga-element-modify-wrapper--muted': muted && isEditing,
      }"
      @mouseenter="isModifyHovered = true"
      @mouseleave="isModifyHovered = false"
  >
    <div
        class="orga-element-modify-layer orga-element-modify-content"
        :aria-hidden="showModifyOnHover"
    >
      <OrgaElementHoverTrigger
          :enabled="hasHoverCard"
          :hover-card="OrgaElementHoverCard"
          :label="label"
          :lib-long="libLong"
          :desc="desc"
      >
        <SourceButton
            :text="label ?? ''"
            :size="size"
            :type="buttonType"
            :disabled="disabled"
            class="orga-element block-content"
            @click="goTo"
        />
      </OrgaElementHoverTrigger>
    </div>
    <button
        type="button"
        class="orga-element orga-element-modify-full orga-element-modify-layer"
        :class="{ 'orga-element-modify-full--compact': compactModify }"
        :aria-label="modifyLabel"
        :aria-hidden="!showModifyOnHover"
        @click="onModifyClick"
    >
      <span
          class="modify-icon modify-icon--full"
          aria-hidden="true"
      >{{ modifyIcon }}</span>
      <span class="modify-label">{{ modifyLabel }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/orga/orga-element";

.orga-element-add-wrapper :deep(.orga-element-add-btn.source-button span:last-child) {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  max-width: 0;
  transition: max-width 0.4s steps(28, end);
  vertical-align: bottom;
}

.orga-element-add-wrapper.is-add-hovered :deep(.orga-element-add-btn.source-button span:last-child) {
  max-width: 22ch;
}

.orga-element-modify-wrapper--in-scope :deep(.source-button.orga-element) {
  box-shadow: inset 0 0 0 2px var(--color-primary);
}

:deep(.source-button.block-content),
:deep(.source-badge.block-content) {
  width: 100%;
  min-width: 0;
  white-space: normal;
}

:deep(.source-button.block-content > div) {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.2;
  text-align: center;
  width: 100%;
  margin: 0;
}

:deep(.source-badge.block-content) {
  * {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.2;
    text-align: center;
  }
}
</style>
